<template>
    <main class="min-h-screen">
        <div class="mx-auto max-w-dashboard pt-8 pb-8">
            <div class="flex justify-between max-sm:px-2">
                <DashboardHeader />
                <TrainDashboardSettingsModal ref="settingsModalRef" />
            </div>

            <AppUpdateNotice class="mx-2 mt-4 sm:mx-0" />

            <section
                v-if="!hasFinishedSetup"
                class="mx-2 mt-8 rounded-lg border border-line bg-surface p-5 sm:mx-0"
                aria-labelledby="empty-dashboard-title"
            >
                <div class="flex items-start gap-3">
                    <AppIcon class="mt-0.5 size-5 text-primary" name="map-pin" />
                    <div>
                        <h2 id="empty-dashboard-title" class="font-semibold text-ink">
                            Set up your regular journeys
                        </h2>
                        <p class="mt-1 text-sm text-ink-muted">
                            First, add at least two station groups. Then add a schedule that selects a journey.
                        </p>
                        <p class="mt-3 border-l-2 border-japonica pl-3 text-sm text-ink-muted">
                            For example: add “Home” with Manchester Piccadilly, add “Work” with Euston and Kings Cross, then schedule Home → Work on weekday mornings (RIP HS2 Phase 2b).
                        </p>
                        <button
                            class="appButton appButton--primary mt-4"
                            type="button"
                            @click="settingsModalRef?.open()"
                        >
                            <AppIcon class="size-4" name="settings" />
                            Open journey settings
                        </button>
                    </div>
                </div>
            </section>
            <div v-else-if="!activeJourney" class="mx-2 mt-8 sm:mx-0">
                <JourneySwitcher />
            </div>
            <div
                v-else-if="isLoadingJourneys && !hasJourneyData"
                class="mt-8 rounded-lg border border-line bg-surface p-4 text-ink-muted"
            >
                Loading journeys...
            </div>
            <div v-else class="mt-4 space-y-4">
                <p
                    v-if="isLoadingJourneys && hasJourneyData"
                    class="rounded-lg border border-line bg-surface p-3 text-sm text-ink-muted"
                    role="status"
                >
                    Updating journeys…
                </p>
                <p
                    v-if="journeyLoadError"
                    class="rounded-lg border border-line bg-surface p-4 text-ink-muted"
                >
                    {{ journeyLoadErrorMessage }}
                    <button
                        v-if="journeyLoadError === JourneyLoadError.NoConsumerKey"
                        class="appButton appButton--secondary ml-3 py-1"
                        type="button"
                        @click="settingsModalRef?.openApiSettings()"
                    >
                        Open API settings
                    </button>
                </p>
                <JourneyTimelines
                    v-if="journeys.length > 0"
                    :journeys="journeys"
                    :currentMinutes="currentMinutes"
                />

                <NoJourneysFound
                    v-if="routesWithoutTimetabledJourneys.length > 0"
                    :journeyRoutes="routesWithoutTimetabledJourneys"
                    :departureMinutes="currentMinutes"
                />
            </div>
            <DiagnosticModal class="mx-2 mt-8 sm:mx-0" />
        </div>
    </main>
</template>

<script setup lang="ts">
import {storeToRefs} from "pinia";
import {computed, ref} from "vue";
import AppIcon from "@/components/AppIcon.vue";
import AppUpdateNotice from "@/components/AppUpdateNotice.vue";
import NoJourneysFound from "./journeys/timetables/NoJourneysFound.vue";
import JourneyTimelines from "./journeys/timetables/JourneyTimelines.vue";
import TrainDashboardSettingsModal from "./settings/TrainDashboardSettingsModal.vue";
import {JourneyLoadError, useTrainServicesStore} from "../store/trainServices.store";
import {useDashboardConfigStore} from "../store/dashboardConfig.store";
import {useDashboardClockStore} from "../store/dashboardClock.store";
import {useJourneySelectionStore} from "../store/journeySelection.store";
import DashboardHeader from "./DashboardHeader.vue";
import DiagnosticModal from "./DiagnosticModal.vue";
import {getRoutesWithoutTimetabledJourneys} from "@/trainDashboard/journeys/missingTimetables/getRoutesWithoutTimetabledJourneys";
import JourneySwitcher from "./journeys/switcher/JourneySwitcher.vue";

const trainServicesStore = useTrainServicesStore();
const dashboardClockStore = useDashboardClockStore();
const dashboardConfigStore = useDashboardConfigStore();
const journeySelectionStore = useJourneySelectionStore();

const settingsModalRef = ref<{
    open: () => void;
    openApiSettings: () => void;
} | null>(null);

const {isLoadingJourneys, journeyLoadError, journeys, routes} = storeToRefs(trainServicesStore);
const {currentMinutes} = storeToRefs(dashboardClockStore);
const {config: dashboardConfig} = storeToRefs(dashboardConfigStore);
const {activeJourney} = storeToRefs(journeySelectionStore);

const hasJourneyData = computed(() => journeys.value.length > 0);
const hasFinishedSetup = computed(
    () =>
        dashboardConfig.value.stationGroups.length > 0
        && dashboardConfig.value.schedules.length > 0
);
const routesWithoutTimetabledJourneys = computed(() =>
    getRoutesWithoutTimetabledJourneys(routes.value, journeys.value)
);

const journeyLoadErrorMessages: Record<JourneyLoadError, string> = {
    [JourneyLoadError.NoConsumerKey]: "Add your Consumer key in Settings → API.",
    [JourneyLoadError.DataCouldntBeLoaded]: "Train data could not be loaded. Try again later.",
};
const journeyLoadErrorMessage = computed(() =>
    journeyLoadError.value ? journeyLoadErrorMessages[journeyLoadError.value] : null
);
</script>
